import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { FormattedMessage, useIntl } from "react-intl";
import { authApi } from "@/lib/api/auth";
import { isApiError } from "@/lib/api/client";
import { showToast } from "@/lib/utils/toast";
import loginImage from "@/assets/exercise-background-photos/login-page-2.png";
import { AuthFormCard } from "@/components/AuthFormCard";
import { AuthHeader } from "@/components/AuthHeader";
import { AuthBackButton } from "@/components/AuthBackButton";
import { AuthErrorAlert } from "@/components/AuthErrorAlert";
import { AuthPasswordInput } from "@/components/AuthPasswordInput";

export const Route = createFileRoute("/auth/reset-password")({
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: typeof search.token === "string" ? search.token : undefined,
  }),
  component: ResetPassword,
});

function ResetPassword() {
  const navigate = useNavigate();
  const intl = useIntl();
  const { token } = Route.useSearch();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetSchema = z
    .object({
      password: z
        .string()
        .min(6, intl.formatMessage({ id: "validation.password.minLength" })),
      confirmPassword: z
        .string()
        .min(
          1,
          intl.formatMessage({ id: "validation.confirmPassword.required" }),
        ),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: intl.formatMessage({ id: "validation.confirmPassword.mustMatch" }),
      path: ["confirmPassword"],
    });

  type ResetFormData = z.infer<typeof resetSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetFormData>({ resolver: zodResolver(resetSchema) });

  const onSubmit = async (data: ResetFormData) => {
    if (!token) {
      setError(intl.formatMessage({ id: "resetPassword.missingToken" }));
      return;
    }
    setError(null);
    setIsLoading(true);
    try {
      await authApi.resetPassword(token, data.password);
      showToast.success(intl.formatMessage({ id: "resetPassword.success" }));
      navigate({ to: "/trainer/auth/login" });
    } catch (err) {
      // The backend returns auth.resetPassword.invalidToken for an expired,
      // used, or unknown token; anything else is a generic failure
      const key =
        isApiError(err) && err.message === "auth.resetPassword.invalidToken"
          ? "resetPassword.invalidToken"
          : "resetPassword.error";
      setError(intl.formatMessage({ id: key }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col relative">
      <AuthHeader
        image={loginImage}
        imageAlt="Reset Password"
        title={<FormattedMessage id="resetPassword.title" />}
        subtitle={<FormattedMessage id="resetPassword.subtitle" />}
      />

      <AuthFormCard>
        <div className="flex flex-col">
          <AuthBackButton onClick={() => navigate({ to: "/trainer/auth/login" })} />

          {!token ? (
            <div className="space-y-4">
              <AuthErrorAlert>
                <FormattedMessage id="resetPassword.missingToken" />
              </AuthErrorAlert>
              <Button
                type="button"
                className="w-full"
                onClick={() => navigate({ to: "/auth/forgot-password" })}
              >
                <FormattedMessage id="resetPassword.requestNew" />
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {error && <AuthErrorAlert>{error}</AuthErrorAlert>}

              <AuthPasswordInput
                {...register("password")}
                placeholder={intl.formatMessage({
                  id: "resetPassword.password.placeholder",
                })}
                disabled={isLoading}
                onFocus={() => setError(null)}
                error={errors.password?.message}
              />

              <AuthPasswordInput
                {...register("confirmPassword")}
                placeholder={intl.formatMessage({
                  id: "resetPassword.confirmPassword.placeholder",
                })}
                disabled={isLoading}
                onFocus={() => setError(null)}
                error={errors.confirmPassword?.message}
              />

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <FormattedMessage id="resetPassword.resetting" />
                ) : (
                  <FormattedMessage id="resetPassword.submit" />
                )}
              </Button>
            </form>
          )}
        </div>
      </AuthFormCard>
    </div>
  );
}
